import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, RefreshCw, Users, Link2, Dumbbell, Shield, FileText } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

const SCHEDA_DIFF_LABELS = {
  missing_in_prod: 'Nuova su TEST',
  content_different: 'Contenuto diverso',
  assignment_different: 'Assegnazione diversa',
  removed_on_test: 'Eliminata su TEST',
  missing_esercizi_on_prod: 'Esercizi da copiare in PROD'
};

const AdminTesterSyncMonitor = () => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [mode, setMode] = useState('workouts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [sampleLimit, setSampleLimit] = useState(20);
  const [migratingKey, setMigratingKey] = useState(null);

  const fetchData = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const params = mode === 'auth'
        ? { action: 'list_auth_differences' }
        : mode === 'reset'
          ? { action: 'list_testers' }
          : mode === 'schede'
            ? { action: 'list_scheda_differences', sample_limit: sampleLimit }
            : { sample_limit: sampleLimit };
      const response = await api.get('/admin_tester_sync_monitor.php', { params });
      setPayload(response.data?.data ?? null);
    } catch (err) {
      console.error('AdminTesterSyncMonitor error:', err);
      setError(err.response?.data?.error || 'Errore nel caricamento del monitor tester');
    } finally {
      setLoading(false);
    }
  }, [api, mode, sampleLimit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = payload?.summary ?? {};
  const users = payload?.users ?? [];

  const generatedAt = useMemo(() => {
    if (!payload?.generated_at) return '-';
    const dt = new Date(payload.generated_at);
    return Number.isNaN(dt.getTime()) ? payload.generated_at : dt.toLocaleString('it-IT');
  }, [payload]);

  const migrateScheda = useCallback(
    async (testUserId, schedaId) => {
      if (!api) return;
      const key = `scheda-${testUserId}-${schedaId}`;
      setMigratingKey(key);
      setError(null);
      try {
        await api.post('/admin_tester_sync_monitor.php', {
          action: 'migrate_scheda',
          test_user_id: testUserId,
          scheda_id: schedaId
        });
        await fetchData();
      } catch (err) {
        console.error('migrateScheda error:', err);
        setError(err.response?.data?.error || 'Errore durante la migrazione della scheda');
      } finally {
        setMigratingKey(null);
      }
    },
    [api, fetchData]
  );

  const removeSchedaFromProd = useCallback(
    async (testUserId, schedaId) => {
      if (!api) return;
      const key = `scheda-remove-${testUserId}-${schedaId}`;
      setMigratingKey(key);
      setError(null);
      try {
        await api.post('/admin_tester_sync_monitor.php', {
          action: 'remove_scheda_from_prod',
          test_user_id: testUserId,
          scheda_id: schedaId
        });
        await fetchData();
      } catch (err) {
        console.error('removeSchedaFromProd error:', err);
        setError(err.response?.data?.error || 'Errore durante la rimozione della scheda da PROD');
      } finally {
        setMigratingKey(null);
      }
    },
    [api, fetchData]
  );

  const syncAllUserSchede = useCallback(
    async (testUserId) => {
      if (!api) return;
      const key = `scheda-all-${testUserId}`;
      setMigratingKey(key);
      setError(null);
      try {
        const response = await api.post('/admin_tester_sync_monitor.php', {
          action: 'sync_all_user_schede',
          test_user_id: testUserId
        });
        const errors = response.data?.data?.errors ?? [];
        if (errors.length > 0) {
          setError(
            `Allineamento parziale: ${errors.length} errori (es. scheda #${errors[0]?.scheda_id}: ${errors[0]?.error})`
          );
        }
        await fetchData();
      } catch (err) {
        console.error('syncAllUserSchede error:', err);
        setError(err.response?.data?.error || 'Errore durante l\'allineamento completo delle schede');
      } finally {
        setMigratingKey(null);
      }
    },
    [api, fetchData]
  );

  const migrateWorkout = useCallback(
    async (testUserId, testWorkoutId) => {
      if (!api) return;
      const key = `${testUserId}-${testWorkoutId}`;
      setMigratingKey(key);
      setError(null);
      try {
        await api.post('/admin_tester_sync_monitor.php', {
          action: 'migrate_workout',
          test_user_id: testUserId,
          test_workout_id: testWorkoutId
        });
        await fetchData();
      } catch (err) {
        console.error('migrateWorkout error:', err);
        setError(err.response?.data?.error || 'Errore durante la migrazione del workout');
      } finally {
        setMigratingKey(null);
      }
    },
    [api, fetchData]
  );

  const syncUserAuthToProd = useCallback(
    async (userId) => {
      if (!api) return;
      const key = `auth-${userId}`;
      setMigratingKey(key);
      setError(null);
      try {
        await api.post('/admin_tester_sync_monitor.php', {
          action: 'sync_user_auth_to_prod',
          user_id: userId
        });
        await fetchData();
      } catch (err) {
        console.error('syncUserAuthToProd error:', err);
        setError(err.response?.data?.error || 'Errore durante la sincronizzazione autenticazione');
      } finally {
        setMigratingKey(null);
      }
    },
    [api, fetchData]
  );

  const resetTesterPasswordInTest = useCallback(
    async (userId, usernameLabel) => {
      if (!api) return;
      const newPassword = window.prompt(`Nuova password TEST per ${usernameLabel || `utente ${userId}`} (min 8 caratteri):`);
      if (!newPassword) return;
      if (newPassword.length < 8) {
        setError('La password deve avere almeno 8 caratteri.');
        return;
      }

      const key = `reset-auth-${userId}`;
      setMigratingKey(key);
      setError(null);
      try {
        await api.post('/admin_tester_sync_monitor.php', {
          action: 'reset_tester_password_in_test',
          user_id: userId,
          new_password: newPassword
        });
        await fetchData();
      } catch (err) {
        console.error('resetTesterPasswordInTest error:', err);
        setError(err.response?.data?.error || 'Errore durante il reset password su TEST');
      } finally {
        setMigratingKey(null);
      }
    },
    [api, fetchData]
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 flex-shrink-0 mb-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Sync dati tester TEST - PROD</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Confronto tra TEST e PROD: schede (crea, modifica, elimina), allenamenti e autenticazione verso PROD.
            Gli allenamenti mancanti possono essere migrati in automatico ogni sera dal cron{' '}
            <code className="text-xs rounded bg-black/5 px-1 dark:bg-white/10">admin_tester_workouts_migrate_cron.php</code>{' '}
            (prima del sync dati PROD→TEST).
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(mode === 'workouts' || mode === 'schede') && (
            <label className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
              Sample
              <select
                value={sampleLimit}
                onChange={(e) => setSampleLimit(Number(e.target.value))}
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-100"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          )}
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </button>
        </div>
      </div>

      <section className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => setMode('schede')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${mode === 'schede' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
          >
            Schede TEST → PROD
          </button>
          <button
            type="button"
            onClick={() => setMode('workouts')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${mode === 'workouts' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
          >
            Allenamenti TEST - PROD
          </button>
          <button
            type="button"
            onClick={() => setMode('auth')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${mode === 'auth' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
          >
            Autenticazione TEST → PROD
          </button>
          <button
            type="button"
            onClick={() => setMode('reset')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${mode === 'reset' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
          >
            Reset password TEST
          </button>
        </div>

        {mode === 'schede' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Tester in TEST</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.tester_users_in_test ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Utenti match TEST/PROD</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.matched_users ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Utenti da allineare</p>
              <p className="text-xl font-semibold text-amber-600 dark:text-amber-400">{summary.users_with_scheda_differences ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Da portare in PROD</p>
              <p className="text-xl font-semibold text-emerald-600 dark:text-emerald-400">{summary.schede_to_push_total ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Da rimuovere da PROD</p>
              <p className="text-xl font-semibold text-red-600 dark:text-red-400">{summary.schede_to_remove_total ?? 0}</p>
            </div>
          </div>
        ) : mode === 'workouts' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Tester in TEST</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.tester_users_in_test ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Utenti match TEST/PROD</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.matched_users ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Utenti con mancanti</p>
              <p className="text-xl font-semibold text-amber-600 dark:text-amber-400">{summary.users_with_missing_workouts ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Allenamenti mancanti totali</p>
              <p className="text-xl font-semibold text-red-600 dark:text-red-400">{summary.missing_workouts_total ?? 0}</p>
            </div>
          </div>
        ) : mode === 'auth' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Tester in TEST</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.tester_users_in_test ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Utenti match TEST/PROD</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.matched_users ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Utenti con differenze auth</p>
              <p className="text-xl font-semibold text-amber-600 dark:text-amber-400">{summary.users_with_auth_differences ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Diff domande sicurezza</p>
              <p className="text-xl font-semibold text-red-600 dark:text-red-400">{summary.security_answers_differences ?? 0}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Tester in TEST</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summary.tester_users_in_test ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs uppercase text-gray-500">Tab attiva</p>
              <p className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">Reset password</p>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-3">Ultimo aggiornamento: {generatedAt}</p>
      </section>

      <section className="flex-1 min-h-0 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="h-full min-h-[240px] flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="m-4 flex items-start gap-3 text-red-600 dark:text-red-400 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        ) : users.length === 0 ? (
          <div className="h-full min-h-[240px] flex flex-col items-center justify-center text-center p-6">
            {mode === 'schede' ? (
              <FileText className="h-10 w-10 text-gray-400 mb-2" />
            ) : mode === 'workouts' ? (
              <Dumbbell className="h-10 w-10 text-gray-400 mb-2" />
            ) : mode === 'auth' ? (
              <Shield className="h-10 w-10 text-gray-400 mb-2" />
            ) : (
              <Users className="h-10 w-10 text-gray-400 mb-2" />
            )}
            <p className="text-gray-800 dark:text-gray-200 font-medium">
              {mode === 'schede'
                ? 'Nessuna scheda da allineare'
                : mode === 'workouts'
                  ? 'Nessun allenamento mancante trovato'
                  : mode === 'auth'
                    ? 'Nessuna differenza autenticazione trovata'
                    : 'Nessun tester trovato'}
            </p>
            <p className="text-sm text-gray-500">
              {mode === 'schede'
                ? 'Le schede del tester su PROD riflettono gia lo stato attuale di TEST.'
                : mode === 'workouts'
                  ? 'I dati TEST risultano gia allineati a PROD per gli utenti tester.'
                  : mode === 'auth'
                    ? 'Password e domande di sicurezza sono allineate tra TEST e PROD.'
                    : 'Verifica che gli account tester abbiano is_tester = 1 su TEST.'}
            </p>
          </div>
        ) : mode === 'schede' ? (
          <div className="h-full overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/40 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Utente</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Schede diverse</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Dettaglio / azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {users.map((item) => (
                  <tr key={`schede-${item.email}`}>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span>{item.test_user?.name || item.test_user?.username || '-'}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => syncAllUserSchede(item.test_user?.id)}
                          disabled={migratingKey === `scheda-all-${item.test_user?.id}`}
                          className="self-start px-2 py-1 rounded bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {migratingKey === `scheda-all-${item.test_user?.id}` ? '...' : 'Allinea tutto'}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-gray-400" />
                        <span>{item.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">
                      <div className="font-semibold text-red-600 dark:text-red-400">{item.different_schede_count}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        +{item.push_count ?? 0} / -{item.remove_count ?? 0}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      <div className="space-y-1 max-w-[640px]">
                        {(item.different_schede || []).slice(0, 5).map((s) => {
                          const isRemove = s.sync_action === 'remove';
                          const actionKey = isRemove
                            ? `scheda-remove-${item.test_user?.id}-${s.scheda_id}`
                            : `scheda-${item.test_user?.id}-${s.scheda_id}`;
                          return (
                          <div
                            key={`${item.email}-${s.scheda_id}`}
                            className={`text-xs rounded border px-2 py-1 flex items-center justify-between gap-2 ${
                              isRemove
                                ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20'
                                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40'
                            }`}
                          >
                            <div>
                              <span className="font-semibold">#{s.scheda_id}</span>
                              {' '}
                              {s.nome || '-'}
                              {!isRemove && (
                                <>
                                  {' | esercizi TEST '}
                                  {s.test_exercises_count}
                                  {' / PROD '}
                                  {s.prod_exercises_count}
                                </>
                              )}
                              {isRemove && s.other_users_assigned > 0 && (
                                <span className="text-red-600 dark:text-red-400">
                                  {' '}
                                  (altri utenti su PROD: {s.other_users_assigned})
                                </span>
                              )}
                              <div className="mt-1 flex flex-wrap gap-1">
                                {(s.difference_types || []).map((type) => (
                                  <span
                                    key={`${s.scheda_id}-${type}`}
                                    className={`rounded-full px-2 py-0.5 ${
                                      isRemove
                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                    }`}
                                  >
                                    {SCHEDA_DIFF_LABELS[type] || type}
                                  </span>
                                ))}
                              </div>
                              {(s.missing_esercizi_on_prod || []).length > 0 && (
                                <p className="mt-1 text-[11px] text-indigo-700 dark:text-indigo-300">
                                  Esercizi assenti su PROD (verranno copiati da TEST):{' '}
                                  {(s.missing_esercizi_on_prod || [])
                                    .map((ex) => `#${ex.esercizio_id} ${ex.nome || ''}`.trim())
                                    .join(', ')}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                isRemove
                                  ? removeSchedaFromProd(item.test_user?.id, s.scheda_id)
                                  : migrateScheda(item.test_user?.id, s.scheda_id)
                              }
                              disabled={migratingKey === actionKey}
                              className={`px-2 py-1 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
                                isRemove ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                              }`}
                            >
                              {migratingKey === actionKey
                                ? '...'
                                : isRemove
                                  ? 'Rimuovi da PROD'
                                  : 'Porta in PROD'}
                            </button>
                          </div>
                          );
                        })}
                        {item.different_schede_count > 5 && (
                          <p className="text-xs text-gray-500">+ altre {item.different_schede_count - 5} schede</p>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : mode === 'workouts' ? (
          <div className="h-full overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/40 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Utente</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Workout TEST</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Workout PROD</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Mancanti</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Dettaglio mancanti</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {users.map((item) => (
                  <tr key={item.email}>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span>{item.test_user?.name || item.test_user?.username || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-gray-400" />
                        <span>{item.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">{item.test_workouts_count}</td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">{item.prod_workouts_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-600 dark:text-red-400">
                      {item.missing_workouts_count}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      <div className="space-y-1 max-w-[560px]">
                        {(item.missing_workouts || []).slice(0, 5).map((w) => (
                          <div
                            key={`${w.workout_id}-${w.signature}`}
                            className="text-xs rounded border border-gray-200 dark:border-gray-700 px-2 py-1 bg-gray-50 dark:bg-gray-800/40 flex items-center justify-between gap-2"
                          >
                            <div>
                              <span className="font-semibold">id {w.workout_id}</span>
                              {' | scheda '}
                              {w.scheda_id}
                              {' | '}
                              <span className="font-medium">{w.data_allenamento}</span>
                              {' | durata: '}
                              {w.durata_totale ?? '-'}
                              {' min'}
                            </div>
                            <button
                              type="button"
                              onClick={() => migrateWorkout(item.test_user?.id, w.workout_id)}
                              disabled={migratingKey === `${item.test_user?.id}-${w.workout_id}`}
                              className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {migratingKey === `${item.test_user?.id}-${w.workout_id}` ? '...' : 'Porta in PROD'}
                            </button>
                          </div>
                        ))}
                        {item.missing_workouts_count > 5 && (
                          <p className="text-xs text-gray-500">+ altri {item.missing_workouts_count - 5} allenamenti</p>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : mode === 'auth' ? (
          <div className="h-full overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/40 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Utente</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Differenze</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Domande TEST</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Domande PROD</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Azione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {users.map((item) => {
                  const userId = item.test_user?.id;
                  const key = `auth-${userId}`;
                  return (
                    <tr key={`auth-${item.email}`}>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span>{item.test_user?.name || item.test_user?.username || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <Link2 className="h-4 w-4 text-gray-400" />
                          <span>{item.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        <div className="flex flex-wrap gap-1">
                          {(item.difference_types || []).map((type) => (
                            <span key={`${item.email}-${type}`} className="text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5">
                              {type}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">{item.test_security_answers_count ?? 0}</td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">{item.prod_security_answers_count ?? 0}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => syncUserAuthToProd(userId)}
                          disabled={migratingKey === key}
                          className="px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {migratingKey === key ? '...' : 'Porta auth in PROD'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-full overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/40 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Utente</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Username</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-500 uppercase">Stato</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase">Azione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {users.map((item) => {
                  const userId = item.id;
                  const resetKey = `reset-auth-${userId}`;
                  return (
                    <tr key={`reset-${item.id}`}>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span>{item.name || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{item.username || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.email || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${item.active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                          {item.active ? 'attivo' : 'inattivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => resetTesterPasswordInTest(userId, item.username || item.name)}
                          disabled={migratingKey === resetKey}
                          className="px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {migratingKey === resetKey ? '...' : 'Reset password TEST'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminTesterSyncMonitor;

